/**
 * Header component - Renders firm letterhead for PDF invoices
 *
 * Parses HTML letterhead content and converts to pdfmake format.
 * Falls back to firm name/tagline/address if HTML parsing fails.
 */

import type { Content, ContentText } from 'pdfmake/interfaces';
import type { LetterheadData } from '../types';

/**
 * Simple HTML tag stripper and text extractor
 * Handles basic HTML from the letterhead editor
 */
function parseHtmlContent(html: string): { lines: string[]; isBold: boolean[]; isItalic: boolean[] } {
    const lines: string[] = [];
    const isBold: boolean[] = [];
    const isItalic: boolean[] = [];

    // Remove style tags and their content
    let cleaned = html.replace(/<style[^>]*>[\s\S]*?<\/style>/gi, '');

    // Replace br and div/p tags with newlines
    cleaned = cleaned.replace(/<br\s*\/?>/gi, '\n');
    cleaned = cleaned.replace(/<\/?(div|p)[^>]*>/gi, '\n');

    // Split by newlines and process each line
    const rawLines = cleaned.split('\n');

    for (const line of rawLines) {
        // Check for bold/strong tags
        const hasBold = /<(b|strong)[^>]*>/i.test(line);
        const hasItalic = /<(i|em)[^>]*>/i.test(line);

        // Strip all remaining HTML tags
        const text = line.replace(/<[^>]+>/g, '').trim();

        // Decode HTML entities
        const decoded = text
            .replace(/&nbsp;/g, ' ')
            .replace(/&amp;/g, '&')
            .replace(/&lt;/g, '<')
            .replace(/&gt;/g, '>')
            .replace(/&quot;/g, '"')
            .replace(/&#39;/g, "'")
            .trim();

        if (decoded) {
            lines.push(decoded);
            isBold.push(hasBold);
            isItalic.push(hasItalic);
        }
    }

    return { lines, isBold, isItalic };
}

/**
 * Create letterhead content for PDF
 *
 * @param data - Letterhead data including HTML or fallback values
 * @returns pdfmake Content array for the letterhead section
 */
export function createHeader(data: LetterheadData): Content[] {
    const content: Content[] = [];

    // Try to parse HTML content first
    if (data.html && data.html.trim()) {
        const { lines, isBold, isItalic } = parseHtmlContent(data.html);

        if (lines.length > 0) {
            // First line is typically the firm name (larger, bold)
            const firstLine = lines[0];
            content.push({
                text: firstLine,
                style: 'letterheadName',
                bold: true,
                alignment: 'center',
            });

            // Remaining lines
            for (let i = 1; i < lines.length; i++) {
                const textStyle: ContentText = {
                    text: lines[i],
                    style: 'letterheadLine',
                    alignment: 'center',
                };

                if (isBold[i]) {
                    textStyle.bold = true;
                }
                if (isItalic[i]) {
                    textStyle.italics = true;
                }

                content.push(textStyle);
            }

            // Add spacing after letterhead
            content.push({ text: '', margin: [0, 10, 0, 0] });

            return content;
        }
    }

    // Fallback to structured data if HTML parsing fails or no HTML
    if (data.firmName) {
        content.push({
            text: data.firmName,
            style: 'letterheadName',
            bold: true,
            alignment: 'center',
        });
    }

    if (data.tagline) {
        content.push({
            text: data.tagline,
            style: 'letterheadTagline',
            italics: true,
            alignment: 'center',
        });
    }

    if (data.address) {
        content.push({
            text: data.address,
            style: 'letterheadAddress',
            alignment: 'center',
        });
    }

    // Add spacing after letterhead
    if (content.length > 0) {
        content.push({ text: '', margin: [0, 10, 0, 0] });
    }

    return content;
}

export default createHeader;
